/**
 * **The Measure tool's state** (`projects/measurement-tool.md`, rulings 7 and 8): two client-local
 * rulers, never persisted. Neither sits on the board untouched (developer, 2026-09-28): the rail's
 * ruler lives from a press to the release of a drag, or from a click to the next click, while Measure
 * is armed; the hold-M ruler lives only while M is held. Both follow the pointer. Distance and sight are never computed here: `measureBetween` (`@lancer-vtt/rules`)
 * counts, so the board always agrees with `lvtt encounter measure`.
 *
 * Every transition returns the SAME reference when nothing changed, so a pointer move inside one
 * cell re-renders nothing and the ruler redraws only when an end crosses into a new cell or unit.
 */
import type { Cell } from "@lancer-vtt/domain";
import type { Measure, MeasureEnd } from "@lancer-vtt/rules";
import { squareKey } from "@lancer-vtt/space";
import { asSquareCell } from "../combatCells";

export type { MeasureEnd };

/**
 * The rail's ruler. `dragging`: the press is held and `to` follows the pointer; the release
 * dismisses it. `open`: the press was a click, so `to` follows the pointer until the next press
 * dismisses it.
 *
 * Touch has no hover, so an `open` ruler cannot follow a finger that is up. There, the second press
 * is `closing`: `to` follows the finger while it is down, and the lift leaves it `fading`, frozen for
 * {@link MEASURE_TOUCH_LINGER_MS} so a tap, tap can be read, then gone. A mouse never reaches either.
 */
export type MeasureRuler = { from: MeasureEnd; to: MeasureEnd; phase: "dragging" | "open" | "closing" | "fading" };

/** How long a touch tap, tap ruler stays readable after the second tap lifts. */
export const MEASURE_TOUCH_LINGER_MS = 1500;

/**
 * How far (screen px) the pointer may travel between press and release and still be a click. Past
 * it the gesture was a drag. Matches the ping hold's `PING_MOVE_TOLERANCE`.
 */
export const MEASURE_CLICK_SLOP_PX = 6;

/** Whether a press at `down` released at `up` (screen px) was a drag rather than a click. */
export function isMeasureDrag(down: { x: number; y: number }, up: { x: number; y: number }): boolean {
  return (up.x - down.x) ** 2 + (up.y - down.y) ** 2 > MEASURE_CLICK_SLOP_PX ** 2;
}

/** The hold-M ruler: bound at keydown, `to` follows the pointer until keyup. */
export type MeasureHold = { from: MeasureEnd; to: MeasureEnd };

/**
 * The end under the pointer: the unit there, anchored on the pointed cell (`at`), else the bare cell.
 * Anchoring keeps the ruler on the cell the pointer is over; the old nearest-footprint-cell end read
 * as arbitrary on Size 2+ units (developer, 2026-09-28).
 */
export function measureEndFor(unitId: string | null, cell: Cell): MeasureEnd {
  return unitId ? { unit: unitId, at: cell } : { cell };
}

/** A stable identity for an end: the unit id and its pointed cell, or the cell's `x,y`. */
export function measureEndKey(end: MeasureEnd): string {
  if ("unit" in end) return end.at ? `unit:${end.unit}@${squareKey(asSquareCell(end.at))}` : `unit:${end.unit}`;
  return `cell:${squareKey(asSquareCell(end.cell))}`;
}

const sameEnd = (a: MeasureEnd, b: MeasureEnd): boolean => measureEndKey(a) === measureEndKey(b);

/**
 * A press while Measure is armed: the second click dismisses an open ruler (a second touch closes it
 * on the finger instead); any other press starts a new one.
 */
export function measurePress(ruler: MeasureRuler | null, end: MeasureEnd, touch = false): MeasureRuler | null {
  if (ruler?.phase === "open") return touch ? { from: ruler.from, to: end, phase: "closing" } : null;
  return { from: end, to: end, phase: "dragging" };
}

/** The pointer entered `end`: a ruler's `to` follows it, except a `fading` one. */
export function measureMove<R extends MeasureRuler | MeasureHold>(ruler: R | null, end: MeasureEnd): R | null {
  if (!ruler || ("phase" in ruler && ruler.phase === "fading") || sameEnd(ruler.to, end)) return ruler;
  return { ...ruler, to: end };
}

/**
 * The press was released: a drag's release dismisses the ruler, a click's leaves it open, and a
 * touch's second lift leaves it fading.
 */
export function measureRelease(ruler: MeasureRuler | null, dragged: boolean): MeasureRuler | null {
  if (ruler?.phase === "closing") return { ...ruler, phase: "fading" };
  if (ruler?.phase !== "dragging") return ruler;
  return dragged ? null : { ...ruler, phase: "open" };
}

/**
 * The readout chip's text, worded like `lvtt encounter measure`: `7 spaces · line of sight · soft cover`.
 * When a pointed cell sits deeper in a unit than its nearest edge, the unit range follows the count:
 * `5 spaces · range 4 · …`.
 */
export function measureReadout(m: Pick<Measure, "distance" | "range" | "sight">): string {
  const count = `${m.distance} space${m.distance === 1 ? "" : "s"}`;
  const spaces = m.range === m.distance ? count : `${count} · range ${m.range}`;
  const sight = m.sight.visible ? `line of sight · ${m.sight.cover === "none" ? "no" : m.sight.cover} cover` : "no line of sight";
  return `${spaces} · ${sight}`;
}
